export type Point = { x: number; y: number };

/**
 * Ordered stroke skeletons, Zaner-Bloser print convention: main stem first, then bowls, then crossbars;
 * T and t lead with their top bar.
 * Each stroke is the pen's centre line in em units — x rightward from the glyph's left edge, y upward
 * from the baseline — authored against Andika's metrics below. A one-point stroke is a tap (a dot).
 */
const X_HEIGHT = 0.5;
export const ASCENDER = 0.72;
const CAP = 0.7;
export const DESCENDER = -0.2;
/** Pen width of the 700-weight face, in em: the ink box of a glyph is its centre line plus half of this on each side. */
export const STEM_EM = 0.13;

/** Samples an elliptical arc. Angles are degrees, 0 = right, increasing counter-clockwise. */
const curve = (
  cx: number,
  cy: number,
  rx: number,
  ry: number,
  from: number,
  to: number
): Point[] => {
  const steps = Math.max(2, Math.ceil(Math.abs(to - from) / 15));
  return Array.from({ length: steps + 1 }, (_, i) => {
    const radians = ((from + ((to - from) * i) / steps) * Math.PI) / 180;
    return { x: cx + rx * Math.cos(radians), y: cy + ry * Math.sin(radians) };
  });
};

const arc = (cx: number, cy: number, r: number, from: number, to: number) =>
  curve(cx, cy, r, r, from, to);

const line = (...points: [number, number][]): Point[] => points.map(([x, y]) => ({ x, y }));

const dot = (x: number, y: number): Point[] => [{ x, y }];

/** The "magic c" bowl shared by a, d, g and q: starts upper-right, travels counter-clockwise, stops where the stem closes it. */
const bowl = () => arc(0.25, 0.25, 0.19, 30, 340);
/** Right-hand stem of those same letters, from the bowl down to the given depth. */
const bowlStem = (depth: number) => line([0.44, 0.44], [0.44, depth]);
/** Arch of h, m, n and r: up the stem, over the top, down to the given foot. */
const arch = (cx: number, cy: number, r: number) => arc(cx, cy, r, 180, 0);

/**
 * Spine of S and s, from the top terminal round to the tail: two ellipses of the same size stacked a
 * full ry apart, so both reach the half-way point with a horizontal tangent and the join reads as one
 * continuous curve rather than a kink. `height` is the glyph's full height above the baseline.
 */
const spine = (cx: number, height: number, rx: number): Point[] => {
  const ry = height / 4;
  return [...curve(cx, height - ry, rx, ry, 20, 270), ...curve(cx, ry, rx, ry, 90, -160)];
};

export const GLYPH_STROKES: Record<string, Point[][]> = {
  a: [bowl(), bowlStem(0)],
  b: [line([0.06, ASCENDER], [0.06, 0]), arc(0.25, 0.25, 0.19, 180, -180)],
  c: [arc(0.27, 0.25, 0.19, 60, 300)],
  d: [bowl(), line([0.44, ASCENDER], [0.44, 0])],
  e: [line([0.07, 0.27], [0.45, 0.27]), arc(0.26, 0.25, 0.19, 10, 320)],
  f: [
    [...arc(0.3, 0.53, 0.18, 30, 180), ...line([0.12, 0.53], [0.12, 0])],
    line([0.0, 0.48], [0.42, 0.48]),
  ],
  g: [bowl(), [...bowlStem(-0.12), ...arc(0.3, -0.12, 0.14, 0, -170)]],
  h: [
    line([0.06, ASCENDER], [0.06, 0]),
    [...arch(0.25, 0.27, 0.19), ...line([0.44, 0.27], [0.44, 0])],
  ],
  i: [line([0.14, X_HEIGHT], [0.14, 0]), dot(0.14, 0.66)],
  j: [[...line([0.2, X_HEIGHT], [0.2, -0.1]), ...arc(0.08, -0.1, 0.12, 0, -110)], dot(0.2, 0.66)],
  k: [
    line([0.06, ASCENDER], [0.06, 0]),
    line([0.44, X_HEIGHT], [0.06, 0.2]),
    line([0.06, 0.2], [0.44, 0]),
  ],
  l: [line([0.12, ASCENDER], [0.12, 0])],
  m: [
    line([0.06, X_HEIGHT], [0.06, 0]),
    [...arch(0.2, 0.36, 0.14), ...line([0.34, 0.36], [0.34, 0])],
    [...arch(0.48, 0.36, 0.14), ...line([0.62, 0.36], [0.62, 0])],
  ],
  n: [
    line([0.06, X_HEIGHT], [0.06, 0]),
    [...arch(0.25, 0.36, 0.19), ...line([0.44, 0.36], [0.44, 0])],
  ],
  o: [arc(0.26, 0.25, 0.19, 90, 450)],
  p: [line([0.06, X_HEIGHT], [0.06, DESCENDER]), arc(0.25, 0.25, 0.19, 180, -180)],
  q: [bowl(), bowlStem(DESCENDER)],
  r: [line([0.06, X_HEIGHT], [0.06, 0]), arc(0.25, 0.31, 0.19, 180, 60)],
  s: [spine(0.25, X_HEIGHT, 0.17)],
  t: [
    line([0.04, X_HEIGHT], [0.38, X_HEIGHT]),
    [...line([0.2, ASCENDER], [0.2, 0.12]), ...arc(0.32, 0.12, 0.12, 180, 340)],
  ],
  u: [
    [...line([0.06, X_HEIGHT], [0.06, 0.16]), ...arc(0.25, 0.16, 0.19, 180, 360)],
    line([0.44, X_HEIGHT], [0.44, 0]),
  ],
  v: [line([0.05, X_HEIGHT], [0.25, 0]), line([0.25, 0], [0.45, X_HEIGHT])],
  w: [
    line([0.04, X_HEIGHT], [0.2, 0]),
    line([0.2, 0], [0.34, 0.44]),
    line([0.34, 0.44], [0.48, 0]),
    line([0.48, 0], [0.64, X_HEIGHT]),
  ],
  x: [line([0.06, X_HEIGHT], [0.42, 0]), line([0.42, X_HEIGHT], [0.06, 0])],
  y: [line([0.06, X_HEIGHT], [0.26, 0.1]), line([0.46, X_HEIGHT], [0.16, DESCENDER])],
  z: [
    line([0.06, X_HEIGHT], [0.42, X_HEIGHT]),
    line([0.42, X_HEIGHT], [0.06, 0]),
    line([0.06, 0], [0.42, 0]),
  ],

  A: [line([0.04, 0], [0.3, CAP]), line([0.3, CAP], [0.56, 0]), line([0.13, 0.24], [0.47, 0.24])],
  B: [
    line([0.08, CAP], [0.08, 0]),
    [
      ...line([0.08, CAP], [0.28, CAP]),
      ...arc(0.28, 0.53, 0.17, 90, -90),
      ...line([0.28, 0.36], [0.08, 0.36]),
    ],
    [
      ...line([0.08, 0.36], [0.3, 0.36]),
      ...arc(0.3, 0.18, 0.18, 90, -90),
      ...line([0.3, 0], [0.08, 0]),
    ],
  ],
  C: [arc(0.33, 0.35, 0.3, 55, 305)],
  D: [
    line([0.08, CAP], [0.08, 0]),
    [
      ...line([0.08, CAP], [0.26, CAP]),
      ...arc(0.26, 0.35, 0.35, 90, -90),
      ...line([0.26, 0], [0.08, 0]),
    ],
  ],
  E: [
    line([0.1, CAP], [0.1, 0]),
    line([0.1, CAP], [0.48, CAP]),
    line([0.1, 0.36], [0.42, 0.36]),
    line([0.1, 0], [0.48, 0]),
  ],
  F: [line([0.1, CAP], [0.1, 0]), line([0.1, CAP], [0.48, CAP]), line([0.1, 0.36], [0.42, 0.36])],
  G: [[...arc(0.33, 0.35, 0.3, 55, 315), ...line([0.54, 0.35])], line([0.36, 0.35], [0.54, 0.35])],
  H: [line([0.08, CAP], [0.08, 0]), line([0.08, 0.36], [0.5, 0.36]), line([0.5, CAP], [0.5, 0])],
  I: [line([0.06, CAP], [0.34, CAP]), line([0.2, CAP], [0.2, 0]), line([0.06, 0], [0.34, 0])],
  J: [
    line([0.16, CAP], [0.56, CAP]),
    [...line([0.38, CAP], [0.38, 0.14]), ...arc(0.2, 0.14, 0.18, 0, -180)],
  ],
  K: [line([0.08, CAP], [0.08, 0]), line([0.46, CAP], [0.08, 0.34]), line([0.08, 0.34], [0.48, 0])],
  L: [line([0.1, CAP], [0.1, 0]), line([0.1, 0], [0.48, 0])],
  M: [
    line([0.06, CAP], [0.06, 0]),
    line([0.06, CAP], [0.33, 0.2]),
    line([0.33, 0.2], [0.6, CAP]),
    line([0.6, CAP], [0.6, 0]),
  ],
  N: [line([0.08, CAP], [0.08, 0]), line([0.08, CAP], [0.5, 0]), line([0.5, 0], [0.5, CAP])],
  O: [arc(0.35, 0.35, 0.3, 90, 450)],
  P: [
    line([0.08, CAP], [0.08, 0]),
    [
      ...line([0.08, CAP], [0.28, CAP]),
      ...arc(0.28, 0.52, 0.18, 90, -90),
      ...line([0.28, 0.34], [0.08, 0.34]),
    ],
  ],
  Q: [arc(0.35, 0.35, 0.3, 90, 450), line([0.45, 0.14], [0.62, -0.06])],
  R: [
    line([0.08, CAP], [0.08, 0]),
    [
      ...line([0.08, CAP], [0.28, CAP]),
      ...arc(0.28, 0.52, 0.18, 90, -90),
      ...line([0.28, 0.34], [0.08, 0.34]),
    ],
    line([0.3, 0.34], [0.54, 0]),
  ],
  S: [spine(0.3, CAP, 0.21)],
  T: [line([0.04, CAP], [0.52, CAP]), line([0.28, CAP], [0.28, 0])],
  U: [
    [
      ...line([0.08, CAP], [0.08, 0.2]),
      ...arc(0.33, 0.2, 0.25, 180, 360),
      ...line([0.58, 0.2], [0.58, CAP]),
    ],
  ],
  V: [line([0.05, CAP], [0.3, 0]), line([0.3, 0], [0.55, CAP])],
  W: [
    line([0.04, CAP], [0.22, 0]),
    line([0.22, 0], [0.38, 0.6]),
    line([0.38, 0.6], [0.54, 0]),
    line([0.54, 0], [0.72, CAP]),
  ],
  X: [line([0.06, CAP], [0.54, 0]), line([0.54, CAP], [0.06, 0])],
  Y: [line([0.06, CAP], [0.3, 0.36]), line([0.54, CAP], [0.3, 0.36]), line([0.3, 0.36], [0.3, 0])],
  Z: [line([0.06, CAP], [0.48, CAP]), line([0.48, CAP], [0.06, 0]), line([0.06, 0], [0.48, 0])],

  "0": [curve(0.26, 0.35, 0.2, 0.35, 90, 450)],
  "1": [line([0.08, 0.54], [0.26, CAP], [0.26, 0])],
  "2": [[...curve(0.26, 0.52, 0.2, 0.18, 150, -30), ...line([0.06, 0], [0.48, 0])]],
  // Both bowls stop at (0.26, 0.36), so the waist is one clean reversal rather than a jog between arcs.
  "3": [[...curve(0.26, 0.53, 0.18, 0.17, 150, -90), ...curve(0.26, 0.18, 0.2, 0.18, 90, -150)]],
  "4": [line([0.34, CAP], [0.06, 0.24], [0.46, 0.24]), line([0.34, CAP], [0.34, 0])],
  // The bowl is entered at its own top, so the run out of the stem foot is already the bowl's tangent.
  "5": [
    [...line([0.1, CAP], [0.1, 0.4]), ...arc(0.26, 0.2, 0.2, 90, -150)],
    line([0.1, CAP], [0.44, CAP]),
  ],
  "6": [[...curve(0.28, 0.52, 0.2, 0.18, 70, 180), ...arc(0.26, 0.2, 0.2, 180, 540)]],
  "7": [line([0.06, CAP], [0.46, CAP], [0.2, 0])],
  "8": [arc(0.26, 0.52, 0.17, 90, 450), arc(0.26, 0.18, 0.19, 90, 450)],
  "9": [arc(0.26, 0.5, 0.19, 0, 360), line([0.45, 0.5], [0.45, 0])],

  "'": [line([0.08, ASCENDER], [0.04, 0.56])],
  "-": [line([0.02, 0.28], [0.3, 0.28])],
  ".": [dot(0.08, 0.04)],
  ",": [line([0.1, 0.08], [0.04, -0.1])],
  "!": [line([0.1, CAP], [0.1, 0.14]), dot(0.1, 0.02)],
  "?": [[...arc(0.22, 0.54, 0.16, 170, -50), ...line([0.22, 0.2])], dot(0.22, 0.02)],
};
